import { EntitySearchType } from '@api';
import {
  ActionHandlers,
  ChatDocumentContextInput,
  ChatInputConfig,
  ChatSubmitEvent,
  PROGRAMMATIC_SUBMIT_EVENT,
} from '@terminal/components/chat';
import { useDropdownKeyboardEvents } from '@terminal/hooks/use-dropdown-keyboard-events/useDropdownKeyboardEvents';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useScheduleDrawerUrlSync } from '@terminal/hooks/use-schedule-drawer-url-sync/useScheduleDrawerUrlSync';
import { useTagsSearch } from '@terminal/hooks/use-tags-search/useTagsSearch';
import {
  EMAIL_TRACKING_PARAM_KEYS,
  queryKeys,
} from '@terminal/routes/query-params';
import {
  POSTHOG_TRACKING_SOURCES,
  TrackingMetadata,
} from '@terminal/services/tracking/posthog/constants';
import { PromptRefinerTrackingArgs } from '@terminal/services/tracking/posthog/use-posthog-events/alfredEvents';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useScheduleDrawerStore } from '@terminal/stores/use-schedule-drawer-store/useScheduleDrawerStore';
import { Statistics } from '@terminal/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';
import { ScheduledQuerySettingsDrawer } from '@terminal/views/alfred/research/scheduled-query-settings-drawer/ScheduledQuerySettingsDrawer';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import { useThemeSearch } from '@terminal/widgets/search-asset-widget/hooks/use-theme-search/useThemeSearch';
import clsx from 'clsx';
import React, {
  ForwardRefExoticComponent,
  forwardRef,
  RefAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ChatActionButtons } from '../../components/chat-action-buttons/ChatActionButtons';
import { ChatInputDropdown } from '../chat-input-dropdown/ChatInputDropdown';
import { toMentionPlainText } from '../mentions/markers';
import { MentionComposerInputHandle } from '../mentions/mention-composer-input/MentionComposerInput';
import { SerializedEditorContent } from '../mentions/mention-composer-input/serialize';
import { MAX_MENTIONS_PER_REQUEST } from '../mentions/types';
import { UploadDisclaimer } from '../upload-disclaimer/UploadDisclaimer';
import styles from './ChatInputComposer.module.scss';
import { PromptRefinementModal } from './prompt-refinement-modal/PromptRefinementModal';
import { decodeQuestion, isAssetNameMessage } from './utils';

export type PromptRefinementContext = Omit<PromptRefinerTrackingArgs, 'source'>;

export interface ChatInputComposerProps {
  className?: string;
  handlers: ActionHandlers;
  config: ChatInputConfig & {
    isLoading: boolean;
  };
  contextCounts?: {
    statistics?: Statistics;
    documentsCount: number;
    subTypeCount: number;
  };
  trackingMetadata?: TrackingMetadata;
  promptRefinementContext?: PromptRefinementContext;
}

const ChatInputComposerBase = forwardRef<
  HTMLTextAreaElement,
  ChatInputComposerProps
>(
  (
    {
      handlers,
      config,
      contextCounts,
      className,
      trackingMetadata,
      promptRefinementContext,
    }: ChatInputComposerProps,
    ref
  ) => {
    const { t } = useTranslation(['chat']);
    const { params, deleteQueryParam } = useQueryParams();
    const { trackPromptRefinerOpened } = usePosthogEvents();
    const isPromptRefinementEnabled = config.enablePromptRefinement ?? true;
    const refinerTracking: PromptRefinerTrackingArgs = {
      source: trackingMetadata?.source ?? POSTHOG_TRACKING_SOURCES.ALFRED,
      conversationId: promptRefinementContext?.conversationId,
      messageId: promptRefinementContext?.messageId,
    };

    const isAssetMessage = useRef(false);
    const [inputValue, setInputValue] = useState('');
    const [isPromptRefinementOpen, setIsPromptRefinementOpen] = useState(false);
    const [showItems, setShowItems] = useState(true);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const mentionInputRef = useRef<MentionComposerInputHandle>(null);
    const mentionsContentRef = useRef<SerializedEditorContent>({
      text: '',
      mentions: [],
    });
    const hasPrefilledQuestionRef = useRef(false);
    const { mode } = useChatNavigation();
    const { autoFocus: shouldAutoFocus = true } = config;
    const encodedQuestion = params.question;
    const { mentionsEnabled, isScheduledTaskEnabled } = useFlags(state => ({
      mentionsEnabled: state.getFeatureFlagState('REASON-2103-alfred-mentions'),
      isScheduledTaskEnabled: state.getFeatureFlagState(
        'scheduled_task_feature'
      ),
    }));
    const { isOpen: isScheduleDrawerOpen, close: closeScheduleDrawer } =
      useScheduleDrawerUrlSync();
    const scheduleDrawerKey = useScheduleDrawerStore(state => state.drawerKey);
    const dropdownApiRef = useDropdownKeyboardEvents(undefined, {
      enabled: !mentionsEnabled,
    });

    // quickAnalysis -- enables asset search + quickAnalysis streaming API
    // quickAnalysis + research -- enables everything, only on dashboard
    // empty -- only question, quickAnalysis streaming API
    const allowedModes = config.allowedModes ?? ['quickAnalysis'];
    const enableAssetSearch =
      !mentionsEnabled &&
      mode === 'quickAnalysis' &&
      allowedModes.includes('quickAnalysis');
    const enableThemeSearch =
      enableAssetSearch && !!(config.enableThemeSearch ?? true);

    const {
      tags: tagList,
      searchTags,
      clearTags,
    } = useTagsSearch({
      searchType: EntitySearchType.Entity,
      additionalConditionChecker: isAssetNameMessage,
    });
    const { themeSearchResults, handleTextChange } = useThemeSearch(
      false,
      enableThemeSearch
    );

    useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
      ref,
      () => inputRef.current,
      [inputRef]
    );

    const clearInput = () => {
      setInputValue('');
    };

    const clearMentionsInput = () => {
      mentionInputRef.current?.clear();
      mentionsContentRef.current = { text: '', mentions: [] };
    };

    const clearDropdown = () => {
      clearTags();
      setShowItems(false);

      if (!config?.persistInputTextOnSubmit) {
        setInputValue('');
      }
    };

    const onSubmitCallbackHandler = () => {
      if (!config?.persistInputTextOnSubmit) {
        clearInput();
        if (mentionsEnabled) {
          clearMentionsInput();
        }
      }

      clearDropdown();

      if (!mentionsEnabled && inputRef.current) {
        inputRef.current.rows = 1;
      }
    };

    const onChatSubmit = (event: ChatSubmitEvent) => {
      event.stopPropagation();
      // Capture before clear — mentions live in an uncontrolled editor + ref.
      const submittedValue = mentionsEnabled
        ? mentionsContentRef.current.text
        : inputValue;
      const submittedMentions = mentionsEnabled
        ? mentionsContentRef.current.mentions.slice(0, MAX_MENTIONS_PER_REQUEST)
        : undefined;

      handlers.onSubmit(
        event,
        submittedValue,
        onSubmitCallbackHandler,
        submittedMentions?.length ? { mentions: submittedMentions } : undefined
      );
    };

    const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      isAssetMessage.current = isAssetNameMessage(value);
      setInputValue(value);
      setShowItems(true);

      if (enableAssetSearch) {
        searchTags(value);
      }

      if (enableThemeSearch) {
        handleTextChange(value);
      }
    };

    const onMentionsChange = (content: SerializedEditorContent) => {
      mentionsContentRef.current = content;
      setInputValue(content.text);
      setShowItems(true);
    };

    const handleUpdateQuestion = (question: string) => {
      if (mentionsEnabled) {
        mentionInputRef.current?.setText(question);
        return;
      }

      setInputValue(question);
    };

    const onMentionsSubmit = () => {
      onChatSubmit(PROGRAMMATIC_SUBMIT_EVENT);
    };

    useEffect(() => {
      if (!encodedQuestion) {
        return;
      }

      const question = decodeQuestion(encodedQuestion);
      const paramsToDelete =
        params[queryKeys.utmSource] === POSTHOG_TRACKING_SOURCES.EMAIL
          ? ['question', ...EMAIL_TRACKING_PARAM_KEYS]
          : 'question';

      deleteQueryParam(paramsToDelete, { replace: true });

      if (!question) {
        return;
      }

      hasPrefilledQuestionRef.current = true;
      isAssetMessage.current = isAssetNameMessage(question);
      setInputValue(question);
      setShowItems(true);

      if (mentionsEnabled) {
        mentionInputRef.current?.setText(question);
        mentionInputRef.current?.focus();
      } else {
        inputRef.current?.focus();
      }
    }, [deleteQueryParam, encodedQuestion, mentionsEnabled, params]);

    const handleSelectRefinedPrompt = (prompt: string) => {
      setIsPromptRefinementOpen(false);
      clearInput();

      if (mentionsEnabled) {
        clearMentionsInput();
      }

      handlers.onSubmit(
        PROGRAMMATIC_SUBMIT_EVENT,
        prompt,
        onSubmitCallbackHandler,
        {
          isPromptRefined: true,
        }
      );
    };

    const handleSelectRefinedPrompts = (prompts: string[]) => {
      const selectedPrompts = prompts
        .map(prompt => prompt.trim())
        .filter(Boolean);

      if (selectedPrompts.length === 0) {
        return;
      }

      setIsPromptRefinementOpen(false);
      clearInput();

      if (mentionsEnabled) {
        clearMentionsInput();
      }

      selectedPrompts.forEach(prompt => {
        handlers.onSubmit(
          PROGRAMMATIC_SUBMIT_EVENT,
          prompt,
          onSubmitCallbackHandler,
          {
            isPromptRefined: true,
            startNewConversation: true,
          }
        );
      });
    };

    const handleOpenPromptRefinement = () => {
      trackPromptRefinerOpened(refinerTracking);
      setIsPromptRefinementOpen(true);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        if (dropdownApiRef.current?.focusedItemIndex === -1) {
          event.stopPropagation();
          onChatSubmit(event);
        }
      }
    };

    useEffect(() => {
      if (hasPrefilledQuestionRef.current) {
        hasPrefilledQuestionRef.current = false;
        return;
      }

      if (!shouldAutoFocus) {
        clearInput();
        if (mentionsEnabled) {
          clearMentionsInput();
        }
        return;
      }

      if (mentionsEnabled) {
        mentionInputRef.current?.focus();
      } else if (inputRef.current) {
        inputRef.current.focus();
      } else {
        clearInput();
      }
    }, [
      inputRef,
      mentionsEnabled,
      params.mode,
      params.conversationId,
      shouldAutoFocus,
    ]);

    const placeholderText = allowedModes.length
      ? mentionsEnabled && mode === 'research'
        ? t('chat:placeholder_researchMentions')
        : t('chat:placeholder', { context: mode })
      : undefined;
    const showPromptRefinement =
      isPromptRefinementEnabled &&
      !!inputValue.trim() &&
      !config.isLoading &&
      !config.isCustomizedDisabled;
    const allowMultiSelect = mode === 'research';

    return (
      <form
        onSubmit={onChatSubmit}
        className={clsx(styles.form, className)}
        data-chat-input-form
      >
        <div
          className={clsx(styles.loaderWrapper, {
            [styles.isMessagePending]: config.isLoading,
          })}
        >
          <ChatInputDropdown
            config={config}
            handlers={{ ...handlers, onSubmitCallbackHandler }}
            inputValue={inputValue}
            tagList={tagList}
            themesList={themeSearchResults}
            clearDropdown={clearDropdown}
            isAssetMessage={isAssetMessage}
            showItems={showItems}
            setInputValue={setInputValue}
            dropdownApiRef={dropdownApiRef}
          >
            <ChatDocumentContextInput
              inputRef={inputRef}
              data-testid="chat-input"
              autoFocus={shouldAutoFocus}
              placeholder={config?.placeholder ?? placeholderText}
              value={inputValue}
              onChange={onInputChange}
              disabled={config.isCustomizedDisabled}
              isMessagePending={config.isLoading}
              contextCounts={contextCounts}
              onKeyDownProp={e => {
                onKeyDown(e);
              }}
              allowedModes={allowedModes}
              enableMentions={mentionsEnabled}
              mentionInputRef={mentionInputRef}
              onMentionsChange={onMentionsChange}
              onMentionsSubmit={onMentionsSubmit}
            >
              <ChatActionButtons
                inputValue={inputValue}
                config={config}
                isMessagePending={config.isLoading}
                abortRequest={handlers.onAbort}
                showRefineButton={showPromptRefinement}
                onRefineClick={handleOpenPromptRefinement}
              />
            </ChatDocumentContextInput>
          </ChatInputDropdown>
        </div>
        <div className={styles.footer}>
          <UploadDisclaimer />
        </div>
        {isPromptRefinementEnabled && (
          <PromptRefinementModal
            isOpen={isPromptRefinementOpen}
            question={toMentionPlainText(inputValue, mentionsEnabled)}
            onClose={() => setIsPromptRefinementOpen(false)}
            onSelectPrompt={handleSelectRefinedPrompt}
            onSelectPrompts={handleSelectRefinedPrompts}
            onUpdateQuestion={handleUpdateQuestion}
            tracking={refinerTracking}
            allowMultiSelect={allowMultiSelect}
          />
        )}
        {isScheduledTaskEnabled && config.alfredEverywhere && (
          <ScheduledQuerySettingsDrawer
            key={scheduleDrawerKey}
            isOpen={isScheduleDrawerOpen}
            onClose={closeScheduleDrawer}
          />
        )}
      </form>
    );
  }
);

export const ChatInputComposer: ForwardRefExoticComponent<
  ChatInputComposerProps & RefAttributes<HTMLTextAreaElement>
> = ChatInputComposerBase;
